<template>
  <span class="button-group">
    <div class="row" v-if="syncing">
      <loader></loader>
    </div>
    <div v-else>
      <router-link
        class="btn btn-default btn-sm"
        :to="{ name: this.consultType, params: { client_id: consult.client, season: consultSeason } }"
      >
        Open
      </router-link>
      |

      <button
        class="btn btn-sm btn-success"
        @click="canSubmit ? submitConsult() : ''"
        :class="{ disabled: !canSubmit }"
        v-tooltip="getSubmitTooltip"
        :title="getSubmitTooltip"
      >
        Submit
      </button>
      |

      <button
        class="btn btn-sm btn-primary"
        @click="canSubmitAllocations ? submitAllocations() : ''"
        v-tooltip="canSubmitAllocations ? 'Submit Allocations' : 'Allocations already submitted'"
        :disabled="!canSubmitAllocations"
      >
        Submit Allocations
      </button>
      |

      <VDropdown style="display: inline" :offset="[0, 16]">
        <button class="btn btn-sm btn-default tooltip-target b3" v-tooltip="'More Options'" title="More Options">
          <span class="glyphicon glyphicon-option-vertical"></span>
        </button>

        <template #popper>
          <ul class="nav nav-pills nav-stacked">
            <li>
              <button class="btn btn-default btn-block" @click="backup" v-tooltip="'Make a backup of entered data'">
                Send Diagnostic
              </button>
            </li>
            <li>
              <button
                class="btn btn-sm btn-danger btn-block"
                v-tooltip="'Delete local copy of the Consult'"
                @click="confirmDeleteConsult"
              >
                Delete
              </button>
            </li>
          </ul>
        </template>
      </VDropdown>
    </div>
  </span>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { indexedDB } from '@/_helpers'
import { consultFormService } from '@/_services'
import currentConsult from '@/_store/current_consult.module'
import winterConsult from '@/_store/winter_consult.module'

export default {
  props: ['consult', 'consultSeason'],
  data() {
    return {
      syncing: false,
      consultType: 'winter_consult',
    }
  },
  methods: {
    ...mapActions('client', ['updateClientLastConsult']),

    submitConsult: function () {
      // set currentConsult store
      Object.assign(currentConsult.state, winterConsult.stateMixin)
      Object.assign(currentConsult.actions, winterConsult.actionsMixin)
      Object.assign(currentConsult.mutations, winterConsult.mutationsMixin)
      Object.assign(currentConsult.getters, winterConsult.gettersMixin)

      if (this.$store.hasModule('currentConsult')) {
        this.$store.unregisterModule('currentConsult')
      }
      this.$store.registerModule('currentConsult', currentConsult)

      this.$store.commit('currentConsult/setConsultType', 'winterConsult')
      this.syncing = true
      let self = this

      let consultParams = { clientId: this.consult.client, season: this.consultSeason }

      this.$store.dispatch('currentConsult/setClient', this.consult.client).then(() => {
        self.$store
          .dispatch('currentConsult/saveConsultData', consultParams)
          .then(() => {
            self.$store.dispatch('alert/success', {
              message: 'Success! Data has been synced to the server.',
            })
            self.deleteConsult()

            self.$emit('consult-changed')
            self.$router.push('/')
          })
          .catch((error) => {

            if (error.message == 'Consult not found on IDB') {

              self.$store.dispatch('alert/error', {
                message: 'Consult could not be found, this likely means it was submitted or deleted on a different tab..',
              })

              self.$emit('consult-changed')
              return
            }
            if (! ('response' in error)) {
              self.$store.dispatch('alert/error', {
                message: 'An error occurred. Contact the administrator.',
              })
              return
            }
            if (error.response.status == 500) {
              self.$store.dispatch('alert/error', {
                message: 'An error occurred. Contact the administrator.',
              })
              let hideAlert = true
              self.backup(hideAlert)
            } else {
              if (error.response.data['allocations_synced']) {
                self.$store.dispatch('alert/error', { message: 'Allocations already synced for this consult.' })
                self.consult.models.Misc.read_only = true
              }
              else if (error.response.data['consult_already_finished']) {
                self.$store.dispatch('alert/error', { message: 'Consult already finished.' })
                self.consult.models.Misc.read_only = true
                self.$emit('consult-changed')
                return
                // TODO: store data on idb?
              } else {
                self.$store
                  .dispatch('currentConsult/addBackendErrors', {
                    clientId: self.consult.client,
                    errors: error.response.data,
                  })
                  .then(() => {
                    self.$router.push({
                      name: 'winter_consult',
                      params: { client_id: self.consult.client, season: self.consultSeason },
                    })
                  })
              }
            }
          })
          .finally(() => {
            self.syncing = false
          })
      })
    },

    submitAllocations: function () {
      // set currentConsult store
      Object.assign(currentConsult.state, winterConsult.stateMixin)
      Object.assign(currentConsult.actions, winterConsult.actionsMixin)
      Object.assign(currentConsult.mutations, winterConsult.mutationsMixin)
      Object.assign(currentConsult.getters, winterConsult.gettersMixin)

      if (this.$store.hasModule('currentConsult')) {
        this.$store.unregisterModule('currentConsult')
      }
      this.$store.registerModule('currentConsult', currentConsult)

      this.$store.commit('currentConsult/setConsultType', 'winterConsult')
      this.syncing = true
      let self = this

      let consultParams = { clientId: this.consult.client, season: this.consultSeason }

      this.$store.dispatch('currentConsult/setClient', this.consult.client).then(() => {
        self.$store
          .dispatch('currentConsult/saveConsultAllocations', consultParams)
          .then(() => {
            self.$store.dispatch('alert/success', {
              message: 'Consult allocations successfully saved.',
            })
            self.$store.commit('currentConsult/setFieldValue', {
              stepName: 'DrugAllocations',
              fieldName: 'has_synced_allocations',
              value: true,
            })
            self.$store.dispatch('currentConsult/backupState')
            self.$emit('consult-changed')
            self.$router.push('/')
          })
          .catch((error) => {
            if (error.message == 'Consult not found on IDB') {

              self.$store.dispatch('alert/error', {
                message: 'Consult could not be found, this likely means it was submitted or deleted on a different tab..',
              })

              self.$emit('consult-changed')
              return
            }
            if (! ('response' in error)) {
              self.$store.dispatch('alert/error', {
                message: 'An error occurred. Contact the administrator.',
              })
              return
            }
            if (error.response.status == 500) {
              self.$store.dispatch('alert/error', {
                message: 'An error occurred. Contact the administrator.',
              })
              let hideAlert = true
              self.backup(hideAlert)
            } else {
              if (error.response.data['allocations_synced']) {
                self.$store.dispatch('alert/error', { message: 'Allocations already synced for this consult.' })
                self.consult.models.DrugAllocations.has_synced_allocations = true
              }
              else if (error.response.data['message']) {
								// this is for no_allocation_submit_from_staff
                self.$store.dispatch('alert/error', { message: error.response.data.message })
              }
              else if (error.response.data['consult_already_finished']) {
                self.$store.dispatch('alert/error', { message: 'Consult already finished.' })
                self.consult.models.Misc.read_only = true
                // TODO: store data on idb?
              } else {
                self.$store
                  .dispatch('currentConsult/addBackendErrors', {
                    clientId: self.consult.client,
                    errors: error.response.data,
                  })
                  .then(() => {
                    self.$router.push({
                      name: 'winter_consult',
                      params: { client_id: self.consult.client, season: self.consultSeason },
                    })
                  })
              }
            }
          })
          .finally(() => {
            self.syncing = false

            if (this.$store.hasModule('currentConsult')) {
              this.$store.unregisterModule('currentConsult')
            }
          })
      })
    },

    backup: function (hideAlert) {
      // TODO: maybe add a consult type to indexedDB on backend
      this.syncing = true

      consultFormService
        .consultBackup(this.consult, this.consult.client)
        .then(() => {
          if (!hideAlert) {
            this.$store.dispatch('alert/success', { message: 'Data has been synced to the backup server.' })
          }
        })
        .catch(() => {
          if (!hideAlert) {
            this.$store.dispatch('alert/error', { message: 'An error occurred. Contact the administrator.' })
          }
        })
        .finally(() => {
          this.syncing = false
        })
    },

    deleteConsult: function () {
      var self = this
      var dbKey = this.consult.client
      var consultData = { client: this.consult.client, consultType: this.consultType }

      indexedDB.delete(dbKey).then(() => {
        self.$emit('consult-deleted', consultData)
      })
    },
    confirmDeleteConsult: function () {
      if (confirm('Do you really want to delete?')) {
        this.deleteConsult()
      }
    },
  },
  computed: {
    ...mapState('winterConsult', ['backendErrors']),
    ...mapGetters('connection', ['online']),
    canSubmitAllocations: function () {
      return Boolean(
        !this.syncing &&
          this.online &&
          !this.consult.models.DrugAllocations.has_synced_allocations &&
          !(this.consult.models.Misc.state == 'finished')
      )
    },
    canSubmit: function () {
      if (this.consult.models.Misc.read_only) {
        return false
      }
      return this.online && this.consult.models.Misc.signed
    },
    getSubmitTooltip: function () {
      if (!this.online) {
        return "Please wait until you're online to submit"
      } else if (this.consult.models.Misc.read_only) {
        return 'Consult is in read only mode'
      } else if (!this.consult.models.Misc.signed) {
        return 'Please complete required fields and sign the consult'
      }
      return null
    },
  },
}
</script>
